import { createFileRoute, useRouter } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { eq } from "drizzle-orm";
import { drizzle } from "drizzle-orm/node-postgres";
import { createSignal, For, onMount, Show, type JSX } from "solid-js";
import { toast } from "solid-sonner";
import {
  IconBuilding2,
  IconCalendar,
  IconCheck,
  IconCpu,
  IconHardDrive,
  IconLandmark,
  IconRotateCcw,
  IconSparkles,
} from "~/components/icons";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
} from "~/components/standard-route/detail/layout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { countBankConnectionsForUser } from "~/features/banking/banking.server";
import { countBusinessesForUser } from "~/features/business/business.server";
import { pool } from "~/lib/db";
import { traceRelational } from "~/lib/db-tracing";
import { cn, formatDisplayCalendarDate } from "~/lib/utils";
import * as authSchema from "~/schemas/auth-schema";
import { user } from "~/schemas/auth-schema";
import { getAiCreditsUsedInPeriod } from "~/server/ai-billing";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  getFreeTierStorageBytes,
  getIncludedAiCredits,
  getIncludedStorageBytes,
  getPlanByTier,
  hasActiveSubscription,
  PLANS,
  type PlanTier,
} from "~/server/plans";
import { getUserStorageTotalBytes } from "~/server/s3";
import { syncSubscriptionFromStripe } from "~/server/stripe";

const db = drizzle(pool, { schema: authSchema });

// Subscription status with a Stripe live-state reconciliation safety net for
// missed webhook events (one retrieve call per page load).
const getBillingUser = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => {
    const dbUser = await traceRelational("billing.getBillingUser", () =>
      db.query.user.findFirst({
        where: eq(user.id, context.user.id),
      }),
    );
    const synced = await syncSubscriptionFromStripe(dbUser);
    return {
      subscriptionStatus: synced.subscriptionStatus,
      subscriptionTier: synced.subscriptionTier,
      stripeSubscriptionId: dbUser?.stripeSubscriptionId ?? null,
      stripeCustomerId: dbUser?.stripeCustomerId ?? null,
      subscriptionCancelAt: synced.subscriptionCancelAt,
      userId: context.user.id,
    };
  });

// Slow — Stripe meter summaries + S3 scan, loaded lazily after mount
const getBillingUsage = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => {
    const dbUser = await traceRelational("billing.getUsage", () =>
      db.query.user.findFirst({
        where: eq(user.id, context.user.id),
      }),
    );
    // Reconcile with live Stripe first. The DB tier can lag behind reality —
    // Stripe retries failed webhook deliveries, so a stale event can write an
    // old tier after a plan change, and this read only has the loader's sync
    // to lean on otherwise. Syncing here keeps the usage rows (storage quota,
    // AI allowance, business/bank limits) in lockstep with the plan label.
    const synced = await syncSubscriptionFromStripe(dbUser);

    const isPaid = hasActiveSubscription(synced.subscriptionStatus);

    const tier: PlanTier = isPaid
      ? synced.subscriptionTier === "basic" ||
        synced.subscriptionTier === "pro" ||
        synced.subscriptionTier === "business"
        ? synced.subscriptionTier
        : "basic"
      : "free";

    const [storageBytes, aiCredits, businessCount, bankConnectionCount] =
      await Promise.all([
        getUserStorageTotalBytes(context.user.id).catch(() => 0),
        // Exact period credits from the ai_usage ledger — same source for
        // every tier (the Stripe meter only holds BILLABLE overage). Window:
        // Stripe billing period for paid, UTC calendar month for free.
        getAiCreditsUsedInPeriod(context.user.id).catch(() => 0),
        countBusinessesForUser(context.user.id).catch(() => 0),
        countBankConnectionsForUser(context.user.id).catch(() => 0),
      ]);

    return {
      storageBytes,
      aiCredits,
      // Included allowance is a plan entitlement; usage beyond it is metered.
      aiCreditAllowance: getIncludedAiCredits(tier),
      storageQuotaBytes: isPaid
        ? getIncludedStorageBytes(tier)
        : getFreeTierStorageBytes(),
      businessCount,
      businessLimit: getPlanByTier(tier)?.businessLimit ?? null,
      businessOveragePriceUsd: getPlanByTier(tier)?.extraBusinessPriceUsd ?? 0,
      bankConnectionCount,
      bankConnectionLimit: getPlanByTier(tier)?.includedBankConnections ?? 0,
      tier,
    };
  });

export const Route = createFileRoute("/_dashboard/dashboard/settings/billing")({
  staticData: {
    breadcrumb: "Billing",
  },
  loader: async () => {
    const billing = await getBillingUser();
    return { billing };
  },
  component: BillingPage,
});

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function formatCredits(n: number): string {
  n = Math.round(n); // display only — ledger/meter keep exact fractional credits
  const compact = (v: number, suffix: string) =>
    `${v.toFixed(1).replace(/\.0$/, "")}${suffix}`;
  if (n >= 1_000_000) return compact(n / 1_000_000, "M");
  if (n >= 1_000) return compact(n / 1_000, "K");
  return n.toLocaleString();
}

function formatCost(usd: number): string {
  if (usd <= 0) return "$0.00";
  if (usd < 0.01) return `~$${usd.toFixed(4)}`;
  return `~$${usd.toFixed(2)}`;
}

// Pricing constants (must match plans.ts)
const CREDIT_PRICE_USD = 0.0001; // per AI credit
const STORAGE_PRICE_BYTE_USD = 0.05 / 1_000_000_000; // $0.05/decimal GB -> per byte

// Plausible placeholder values shown blurred before real usage loads.
// Module-level so SSR and client render the same markup — no hydration mismatch.
const placeholderUsage = {
  // Storage skeleton bar at 30%: 0.3 × 1 GiB quota
  storageBytes: 322_122_547, // 0.30 GB — clearly different from real value
  // AI skeleton bar at 60%: 0.6 × 1.0K allowance
  aiCredits: 600, // 600 — clearly different from real value
  aiCreditAllowance: 1000, // 1.0K — clearly different from real value
  storageQuotaBytes: 1 * 1024 * 1024 * 1024, // 1 GB
  businessCount: 1, // fill the skeleton bar at 100% (limit 1)
  businessLimit: 1, // 1 — clearly different from real value
  businessOveragePriceUsd: 2, // 2 — clearly different from real value
  bankConnectionCount: 1, // fill the skeleton bar at 100% (limit 1)
  bankConnectionLimit: 1, // 1 — clearly different from real value
  tier: "free" as const,
};

function BillingPage() {
  const router = useRouter();
  const data = Route.useLoaderData();
  const billing = () => data().billing;
  const isActive = () =>
    billing().subscriptionStatus === "active" ||
    billing().subscriptionStatus === "trialing";

  // Lazy-loaded usage — strictly client-side via onMount, never runs on SSR.

  // createResource caused a hydration flash; onMount + signal avoids that entirely.
  const [usageData, setUsageData] = createSignal<{
    storageBytes: number;
    aiCredits: number;
    aiCreditAllowance: number;
    storageQuotaBytes: number;
    businessCount: number;
    businessLimit: number | null;
    businessOveragePriceUsd: number;
    bankConnectionCount: number;
    bankConnectionLimit: number;
    tier: string;
  } | null>(null);
  onMount(async () => {
    try {
      setUsageData(await getBillingUsage());
    } catch {
      setUsageData({
        storageBytes: 0,
        aiCredits: 0,
        aiCreditAllowance: 1000,
        storageQuotaBytes: 1 * 1024 * 1024 * 1024,
        businessCount: 0,
        businessLimit: 1,
        businessOveragePriceUsd: 0,
        bankConnectionCount: 0,
        bankConnectionLimit: 1,
        tier: "free",
      });
    }
  });
  const displayUsage = () => usageData() ?? placeholderUsage;
  // Branch on the synced subscription state while usage is still loading so
  // the blurred skeleton renders the SAME row structure (free vs paid layout)
  // that the real data will produce — no layout shift when it arrives.
  const isFreeTier = () => {
    const u = usageData();
    return u ? u.tier === "free" : !isActive();
  };
  const currentPlan = () =>
    PLANS.find((p) => p.tier === billing().subscriptionTier) ??
    PLANS.find((p) => p.tier === "basic")!;

  const [loadingTier, setLoadingTier] = createSignal<string | null>(null);
  const [loadingPortal, setLoadingPortal] = createSignal(false);
  const [loadingResume, setLoadingResume] = createSignal(false);
  // Tier awaiting explicit user confirmation before the Subscriptions API is
  // called (plan changes are immediate with proration — no portal in between).
  const [confirmTier, setConfirmTier] = createSignal<PlanTier | null>(null);
  // Reversing a scheduled cancellation is also immediate — ask first.
  const [confirmResume, setConfirmResume] = createSignal(false);

  async function startCheckout(tier: string) {
    setLoadingTier(tier);
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier }),
      });
      const json = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !json.url) {
        toast.error(json.error ?? "Failed to start checkout");
        return;
      }
      window.location.href = json.url;
    } catch {
      toast.error("Failed to start checkout");
    } finally {
      setLoadingTier(null);
    }
  }

  async function openBillingPortal() {
    setLoadingPortal(true);
    try {
      const res = await fetch("/api/stripe/billing-portal", {
        method: "POST",
      });
      const json = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !json.url) {
        toast.error(json.error ?? "Failed to open billing portal");
        return;
      }
      window.location.href = json.url;
    } catch {
      toast.error("Failed to open billing portal");
    } finally {
      setLoadingPortal(false);
    }
  }

  // Reverse a scheduled cancellation in-app — same Subscriptions API the plan
  // changes use, so no portal round-trip needed. The `customer.subscription
  // .updated` webhook (plus the loader's syncSubscriptionFromStripe safety
  // net) clears the stored cancel date.
  async function resumeSubscription() {
    setLoadingResume(true);
    try {
      const res = await fetch("/api/stripe/resume", {
        method: "POST",
      });
      const json = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !json.ok) {
        toast.error(json.error ?? "Failed to keep subscription");
        return;
      }
      toast.success("Subscription kept — you'll stay on your plan");
      await router.invalidate();
      setUsageData(await getBillingUsage().catch(() => usageData()));
    } catch {
      toast.error("Failed to keep subscription");
    } finally {
      setLoadingResume(false);
    }
  }

  // In-place plan change for active subscribers. Checkout Sessions are only
  // for initial signups (a second one would double-bill), and the billing
  // portal only supports canceling subscriptions with metered add-ons — so
  // upgrades/downgrades swap the flat price via the Subscriptions API.
  async function changePlan(tier: string) {
    setLoadingTier(tier);
    try {
      const res = await fetch("/api/stripe/change-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier }),
      });
      const json = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !json.ok) {
        toast.error(json.error ?? "Failed to change plan");
        return;
      }
      toast.success("Plan updated");
      // Re-run the loader so the new tier shows immediately. The webhook
      // (`customer.subscription.updated`) syncs the DB; the loader's
      // syncSubscriptionFromStripe is the safety net on top. It also writes
      // the new tier to the DB, so re-fetch usage AFTER invalidate resolves —
      // otherwise the usage rows (storage quota, AI allowance, business/bank
      // limits) would keep showing the old plan's numbers until reload.
      await router.invalidate();
      setUsageData(await getBillingUsage().catch(() => usageData()));
    } catch {
      toast.error("Failed to change plan");
    } finally {
      setLoadingTier(null);
    }
  }

  // The Subscriptions API applies the change instantly (prorated charge or
  // credit on the next invoice) — there's no portal page in between, so ask
  // for explicit confirmation first. The dialog only opens for plan changes;
  // "Downgrade" to free still goes through the portal, which has its own
  // confirmation UI.
  function confirmPlanChange(tier: PlanTier) {
    setConfirmTier(tier);
  }

  const statusLabel = () => {
    const s = billing().subscriptionStatus;
    if (s === "active") return { text: "Active", variant: "default" as const };
    if (s === "trialing")
      return { text: "Trialing", variant: "secondary" as const };
    if (s === "past_due")
      return { text: "Past Due", variant: "error" as const };
    if (s === "canceled")
      return { text: "Canceled", variant: "outline" as const };
    return { text: "Inactive", variant: "outline" as const };
  };

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-10">
      <StandardRouteDetailHeader
        icon={<IconSparkles class="size-5" />}
        title="Billing"
        badge={statusLabel().text}
        statusBadge={
          <Show when={isActive() && billing().subscriptionCancelAt}>
            <Badge variant="warning" class="gap-1">
              <IconCalendar class="size-3" />
              Cancels{" "}
              {formatDisplayCalendarDate(billing().subscriptionCancelAt!, {
                style: "short",
              })}
            </Badge>
          </Show>
        }
        menuContent={
          <>
            <Show when={isActive() && billing().subscriptionCancelAt}>
              <DropdownMenuItem
                disabled={loadingResume()}
                onClick={() => setConfirmResume(true)}
              >
                <IconRotateCcw class="size-4" />
                {loadingResume() ? "Keeping…" : "Keep my subscription"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </Show>
            <DropdownMenuItem
              disabled={!isActive() || loadingPortal()}
              onClick={openBillingPortal}
            >
              <IconSparkles class="size-4" />
              {loadingPortal() ? "Loading…" : "Manage subscription"}
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <StandardRouteDetailCard
              icon={<IconHardDrive class="size-5" />}
              title="Subscription"
              contentClass="divide-y divide-border"
            >
              <Show when={isActive()}>
                <InfoRow
                  label="Plan"
                  value={`${currentPlan().name} · ${currentPlan().price}`}
                />
              </Show>
              <Show when={billing().subscriptionCancelAt}>
                <InfoRow
                  label={
                    isActive()
                      ? "Cancels at end of billing period"
                      : "Canceled on"
                  }
                  value={formatDisplayCalendarDate(
                    billing().subscriptionCancelAt!,
                  )}
                />
              </Show>
              <div class="py-5 space-y-5">
                <div class="flex items-center gap-3.5">
                  <IconHardDrive class="size-4 text-muted-foreground shrink-0" />
                  <div class="flex flex-col gap-1.5 flex-1 min-w-0">
                    <p class="text-xs text-muted-foreground">Storage used</p>
                    <div
                      class={cn(
                        "transition-[filter] duration-300",
                        !usageData() && "blur-xs",
                      )}
                    >
                      <Show
                        when={!isFreeTier()}
                        fallback={
                          <>
                            <p class="text-sm font-semibold">
                              {formatBytes(displayUsage().storageBytes)} /{" "}
                              {formatBytes(displayUsage().storageQuotaBytes)}
                            </p>
                            <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                class="h-full rounded-full bg-primary transition-all"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (displayUsage().storageBytes /
                                      displayUsage().storageQuotaBytes) *
                                      100,
                                  )}%`,
                                }}
                              />
                            </div>
                            <p class="mt-2 text-xs text-muted-foreground">
                              Free storage limit · upgrade for more
                            </p>
                          </>
                        }
                      >
                        <p class="text-sm font-semibold">
                          {formatBytes(displayUsage().storageBytes)} /{" "}
                          {formatBytes(displayUsage().storageQuotaBytes)}{" "}
                          included
                        </p>
                        <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            class="h-full rounded-full bg-primary transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                (displayUsage().storageBytes /
                                  displayUsage().storageQuotaBytes) *
                                  100,
                              )}%`,
                            }}
                          />
                        </div>
                        <p class="mt-2 text-xs text-muted-foreground">
                          <Show
                            when={
                              Math.max(
                                0,
                                displayUsage().storageBytes -
                                  displayUsage().storageQuotaBytes,
                              ) > 0
                            }
                          >
                            {formatCost(
                              Math.max(
                                0,
                                displayUsage().storageBytes -
                                  displayUsage().storageQuotaBytes,
                              ) * STORAGE_PRICE_BYTE_USD,
                            )}{" "}
                            ·{" "}
                          </Show>
                          $0.05/GB/mo overage
                        </p>
                      </Show>
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-3.5">
                  <IconCpu class="size-4 text-muted-foreground shrink-0" />
                  <div class="flex flex-col gap-1.5 flex-1 min-w-0">
                    <p class="text-xs text-muted-foreground">AI credits used</p>
                    <div
                      class={cn(
                        "transition-[filter] duration-300",
                        !usageData() && "blur-xs",
                      )}
                    >
                      <Show
                        when={isFreeTier()}
                        fallback={
                          <>
                            <p class="text-sm font-semibold">
                              {formatCredits(displayUsage().aiCredits)} /{" "}
                              {formatCredits(displayUsage().aiCreditAllowance)}{" "}
                              included
                            </p>
                            <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                class="h-full rounded-full bg-primary transition-all"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (displayUsage().aiCredits /
                                      displayUsage().aiCreditAllowance) *
                                      100,
                                  )}%`,
                                }}
                              />
                            </div>
                            <p class="mt-2 text-xs text-muted-foreground">
                              <Show
                                when={
                                  Math.max(
                                    0,
                                    displayUsage().aiCredits -
                                      displayUsage().aiCreditAllowance,
                                  ) > 0
                                }
                              >
                                {formatCost(
                                  Math.max(
                                    0,
                                    displayUsage().aiCredits -
                                      displayUsage().aiCreditAllowance,
                                  ) * CREDIT_PRICE_USD,
                                )}{" "}
                                ·{" "}
                              </Show>
                              $0.0001/credit overage
                            </p>
                          </>
                        }
                      >
                        <p class="text-sm font-semibold">
                          {formatCredits(displayUsage().aiCredits)} /{" "}
                          {formatCredits(displayUsage().aiCreditAllowance)}{" "}
                          credits
                        </p>
                        <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            class="h-full rounded-full bg-primary transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                (displayUsage().aiCredits /
                                  displayUsage().aiCreditAllowance) *
                                  100,
                              )}%`,
                            }}
                          />
                        </div>
                        <p class="mt-2 text-xs text-muted-foreground">
                          Free monthly limit · resets monthly
                        </p>
                      </Show>
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-3.5">
                  <IconBuilding2 class="size-4 text-muted-foreground shrink-0" />
                  <div class="flex flex-col gap-1.5 flex-1 min-w-0">
                    <p class="text-xs text-muted-foreground">Businesses</p>
                    <div
                      class={cn(
                        "transition-[filter] duration-300",
                        !usageData() && "blur-xs",
                      )}
                    >
                      <Show
                        when={displayUsage().businessLimit !== null}
                        fallback={
                          <p class="text-sm font-semibold">
                            {displayUsage().businessCount} · unlimited on this
                            plan
                          </p>
                        }
                      >
                        <p class="text-sm font-semibold">
                          {displayUsage().businessCount} /{" "}
                          {displayUsage().businessLimit} included
                        </p>
                        <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            class="h-full rounded-full bg-primary transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                (displayUsage().businessLimit ?? 0) > 0
                                  ? (displayUsage().businessCount /
                                      displayUsage().businessLimit!) *
                                      100
                                  : 0,
                              )}%`,
                            }}
                          />
                        </div>
                        <Show when={displayUsage().businessOveragePriceUsd > 0}>
                          <p class="mt-2 text-xs text-muted-foreground">
                            <Show
                              when={
                                (displayUsage().businessLimit ?? 0) > 0 &&
                                displayUsage().businessCount >
                                  displayUsage().businessLimit!
                              }
                            >
                              {formatCost(
                                (displayUsage().businessCount -
                                  displayUsage().businessLimit!) *
                                  displayUsage().businessOveragePriceUsd,
                              )}{" "}
                              ·{" "}
                            </Show>
                            ${displayUsage().businessOveragePriceUsd.toFixed(2)}
                            /extra business/mo overage
                          </p>
                        </Show>
                        <Show
                          when={displayUsage().businessOveragePriceUsd === 0}
                        >
                          <p class="mt-2 text-xs text-muted-foreground">
                            {(displayUsage().businessLimit ?? 0) > 0 &&
                            displayUsage().businessCount >
                              displayUsage().businessLimit!
                              ? "Limit reached · upgrade to add more"
                              : "Free limit · upgrade for more"}
                          </p>
                        </Show>
                      </Show>
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-3.5">
                  <IconLandmark class="size-4 text-muted-foreground shrink-0" />
                  <div class="flex flex-col gap-1.5 flex-1 min-w-0">
                    <p class="text-xs text-muted-foreground">
                      Bank connections
                    </p>
                    <div
                      class={cn(
                        "transition-[filter] duration-300",
                        !usageData() && "blur-xs",
                      )}
                    >
                      <Show
                        when={!isFreeTier()}
                        fallback={
                          <>
                            <p class="text-sm font-semibold">
                              {displayUsage().bankConnectionCount}
                            </p>
                            <p class="mt-2 text-xs text-muted-foreground">
                              Upgrade to connect your bank
                            </p>
                          </>
                        }
                      >
                        <p class="text-sm font-semibold">
                          {displayUsage().bankConnectionCount} /{" "}
                          {displayUsage().bankConnectionLimit} included
                        </p>
                        <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            class="h-full rounded-full bg-primary transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                (displayUsage().bankConnectionLimit ?? 0) > 0
                                  ? (displayUsage().bankConnectionCount /
                                      displayUsage().bankConnectionLimit) *
                                      100
                                  : 0,
                              )}%`,
                            }}
                          />
                        </div>
                        <p class="mt-2 text-xs text-muted-foreground">
                          <Show
                            when={
                              Math.max(
                                0,
                                displayUsage().bankConnectionCount -
                                  displayUsage().bankConnectionLimit,
                              ) > 0
                            }
                          >
                            {formatCost(
                              Math.max(
                                0,
                                displayUsage().bankConnectionCount -
                                  displayUsage().bankConnectionLimit,
                              ) * currentPlan().extraConnectionPriceUsd,
                            )}{" "}
                            ·{" "}
                          </Show>
                          {currentPlan().extraConnectionPriceDisplay} overage
                        </p>
                      </Show>
                    </div>
                  </div>
                </div>
              </div>
              {/* Total estimate */}
              <Show when={isActive()}>
                <div class="py-5">
                  <div class="rounded-md bg-muted/50 px-4 py-4 space-y-2.5">
                    <div class="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{currentPlan().name}</span>
                      <span>${currentPlan().priceUsd.toFixed(2)}</span>
                    </div>
                    <p class="pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
                      Additional usage
                    </p>
                    <div class="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Storage</span>
                      <span
                        class={cn(
                          "transition-[filter] duration-300",
                          !usageData() && "blur-xs",
                        )}
                      >
                        {formatCost(
                          Math.max(
                            0,
                            displayUsage().storageBytes -
                              displayUsage().storageQuotaBytes,
                          ) * STORAGE_PRICE_BYTE_USD,
                        )}
                      </span>
                    </div>
                    <div class="flex items-center justify-between text-xs text-muted-foreground">
                      <span>AI credits</span>
                      <span
                        class={cn(
                          "transition-[filter] duration-300",
                          !usageData() && "blur-xs",
                        )}
                      >
                        {formatCost(
                          Math.max(
                            0,
                            displayUsage().aiCredits -
                              displayUsage().aiCreditAllowance,
                          ) * CREDIT_PRICE_USD,
                        )}
                      </span>
                    </div>
                    <div class="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Bank connections</span>
                      <span
                        class={cn(
                          "transition-[filter] duration-300",
                          !usageData() && "blur-xs",
                        )}
                      >
                        {formatCost(
                          Math.max(
                            0,
                            displayUsage().bankConnectionCount -
                              displayUsage().bankConnectionLimit,
                          ) * currentPlan().extraConnectionPriceUsd,
                        )}
                      </span>
                    </div>
                    <div class="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Businesses</span>
                      <span
                        class={cn(
                          "transition-[filter] duration-300",
                          !usageData() && "blur-xs",
                        )}
                      >
                        {formatCost(
                          Math.max(
                            0,
                            displayUsage().businessCount -
                              (displayUsage().businessLimit ?? 0),
                          ) * displayUsage().businessOveragePriceUsd,
                        )}
                      </span>
                    </div>
                    <div class="border-t border-border/60 pt-3 flex items-center justify-between">
                      <span class="text-sm font-semibold">Est. total</span>
                      <span
                        class={cn(
                          "text-sm font-bold transition-[filter] duration-300",
                          !usageData() && "blur-xs",
                        )}
                      >
                        {formatCost(
                          currentPlan().priceUsd +
                            Math.max(
                              0,
                              displayUsage().storageBytes -
                                displayUsage().storageQuotaBytes,
                            ) *
                              STORAGE_PRICE_BYTE_USD +
                            Math.max(
                              0,
                              displayUsage().aiCredits -
                                displayUsage().aiCreditAllowance,
                            ) *
                              CREDIT_PRICE_USD +
                            Math.max(
                              0,
                              displayUsage().bankConnectionCount -
                                displayUsage().bankConnectionLimit,
                            ) *
                              currentPlan().extraConnectionPriceUsd +
                            Math.max(
                              0,
                              displayUsage().businessCount -
                                (displayUsage().businessLimit ?? 0),
                            ) *
                              displayUsage().businessOveragePriceUsd,
                        )}
                        /mo
                      </span>
                    </div>
                  </div>
                </div>
              </Show>
              <Show when={isActive()}>
                <div class="py-5">
                  <Button
                    variant="outline"
                    class="w-full"
                    size="sm"
                    disabled={loadingPortal()}
                    onClick={openBillingPortal}
                  >
                    {loadingPortal() ? "Loading…" : "Manage subscription"}
                  </Button>
                </div>
              </Show>
            </StandardRouteDetailCard>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader class="pb-0">
                <CardContent class="px-0 pt-5 pb-7 space-y-6">
                  <For each={PLANS}>
                    {(plan) => {
                      const isCurrent = () =>
                        !plan.comingSoon &&
                        (isActive()
                          ? plan.tier === billing().subscriptionTier
                          : plan.tier === "free");
                      // A cancellation is scheduled for the end of the billing
                      // period — Stripe keeps the subscription active until then.
                      const cancelPending = () =>
                        isActive() && !!billing().subscriptionCancelAt;
                      // The current plan card turns into a "Keep my plan"
                      // action while a cancellation is pending.
                      const isCanceling = () => isCurrent() && cancelPending();
                      // New subscribers buy via Stripe Checkout.
                      const canSubscribe = () =>
                        !isActive() && plan.tier !== "free" && !plan.comingSoon;
                      // Active subscribers change plans in-app via the
                      // Subscriptions API — never a second Checkout (double
                      // billing) and never the portal (metered add-ons make
                      // it cancel-only). Stripe prorates the switch.
                      const canChangePlan = () =>
                        isActive() &&
                        !plan.comingSoon &&
                        !isCurrent() &&
                        plan.tier !== "free";
                      // Downgrading to free = canceling the subscription,
                      // which is exactly what the billing portal does.
                      const canDowngradeToFree = () =>
                        isActive() && plan.tier === "free";

                      return (
                        <div
                          class={`relative rounded-lg border bg-background p-6 ${
                            isCurrent() ? "ring-2 ring-primary" : ""
                          }`}
                        >
                          <Show when={isCurrent()}>
                            <span
                              class={`absolute -top-2.5 left-4 rounded-full px-3 py-0.5 text-xs font-medium text-primary-foreground ${
                                isCanceling()
                                  ? "bg-warning text-warning-foreground"
                                  : "bg-primary"
                              }`}
                            >
                              {isCanceling()
                                ? "Cancels at period end"
                                : "Current plan"}
                            </span>
                          </Show>
                          <Show when={plan.comingSoon}>
                            <span class="absolute -top-2.5 left-4 rounded-full bg-muted px-3 py-0.5 text-xs font-medium text-muted-foreground">
                              Coming soon
                            </span>
                          </Show>

                          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
                            <div class="flex-1 min-w-0">
                              <div class="flex items-baseline gap-3 mb-2">
                                <p class="font-semibold">{plan.name}</p>
                                <p class="text-lg font-bold">{plan.price}</p>
                              </div>
                              <ul class="flex flex-wrap gap-x-6 gap-y-2 mt-3">
                                <For each={plan.features}>
                                  {(feat) => (
                                    <li class="flex items-center gap-1.5 text-xs text-muted-foreground">
                                      <IconCheck class="size-3.5 text-primary shrink-0" />
                                      {feat}
                                    </li>
                                  )}
                                </For>
                              </ul>
                            </div>

                            <Button
                              variant={isCurrent() ? "outline" : "default"}
                              size="sm"
                              class="shrink-0 self-end sm:self-center"
                              disabled={
                                plan.comingSoon ||
                                (isCurrent() && !isCanceling()) ||
                                // A downgrade to free is a cancellation, and
                                // one is already scheduled — nothing to do.
                                (canDowngradeToFree() && cancelPending()) ||
                                loadingTier() !== null ||
                                loadingPortal() ||
                                loadingResume() ||
                                // Catch-all for cards with no available action.
                                // The current card's "Keep my plan" resume is an
                                // action too — don't disable it here.
                                (!isCanceling() &&
                                  !canSubscribe() &&
                                  !canChangePlan() &&
                                  !canDowngradeToFree())
                              }
                              onClick={() => {
                                if (isCanceling()) {
                                  setConfirmResume(true);
                                } else if (canSubscribe()) {
                                  startCheckout(plan.tier);
                                } else if (canChangePlan()) {
                                  confirmPlanChange(plan.tier);
                                } else if (canDowngradeToFree()) {
                                  openBillingPortal();
                                }
                              }}
                            >
                              {plan.comingSoon
                                ? "Coming soon"
                                : isCurrent()
                                  ? isCanceling()
                                    ? loadingResume()
                                      ? "Keeping…"
                                      : "Keep my plan"
                                    : "Current plan"
                                  : loadingTier() === plan.tier
                                    ? "Loading…"
                                    : plan.tier === "free"
                                      ? isActive()
                                        ? cancelPending()
                                          ? "Downgrade pending"
                                          : "Downgrade"
                                        : "Current"
                                      : isActive()
                                        ? "Change plan"
                                        : "Subscribe"}
                            </Button>
                          </div>
                        </div>
                      );
                    }}
                  </For>
                  <Show when={isActive()}>
                    <p class="text-xs text-muted-foreground text-center">
                      To cancel, downgrade to the free plan, or update your
                      payment method, use the{" "}
                      <button
                        class="underline underline-offset-2 hover:text-foreground"
                        onClick={openBillingPortal}
                      >
                        billing portal
                      </button>
                      .
                    </p>
                  </Show>
                  <div class="mt-2 pt-4 border-t border-border/60">
                    <p class="text-xs font-medium text-foreground/90 mb-2">
                      Overage beyond included allowances
                    </p>
                    <div class="space-y-1.5">
                      <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
                        <span class="inline-flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                          <IconCpu class="size-3.5 shrink-0" />
                          AI credits
                        </span>
                        <Badge
                          variant="secondary"
                          class="text-xs font-medium tabular-nums"
                        >
                          $0.0001/credit
                        </Badge>
                      </div>
                      <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
                        <span class="inline-flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                          <IconHardDrive class="size-3.5 shrink-0" />
                          Storage
                        </span>
                        <Badge
                          variant="secondary"
                          class="text-xs font-medium tabular-nums"
                        >
                          $0.05/GB/mo
                        </Badge>
                      </div>
                      <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
                        <span class="inline-flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                          <IconLandmark class="size-3.5 shrink-0" />
                          Bank connections
                        </span>
                        <Badge
                          variant="secondary"
                          class="text-xs font-medium tabular-nums"
                        >
                          $2.00/connection/mo
                        </Badge>
                      </div>
                      <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
                        <span class="inline-flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                          <IconBuilding2 class="size-3.5 shrink-0" />
                          Businesses
                        </span>
                        <Badge
                          variant="secondary"
                          class="text-xs font-medium tabular-nums"
                        >
                          {currentPlan().extraBusinessPriceDisplay ||
                            "$2.00/business/mo"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <Dialog
        open={confirmTier() !== null}
        onOpenChange={(open) => !open && setConfirmTier(null)}
      >
        <Show when={confirmTier()}>
          {(tier) => {
            const target = PLANS.find((p) => p.tier === tier())!;
            const upgrading =
              (target.priceUsd ?? 0) >= (currentPlan().priceUsd ?? 0);
            return (
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Switch to {target.name}?</DialogTitle>
                  <DialogDescription>
                    {upgrading
                      ? "Your switch happens right away. We'll only charge the difference for the rest of this billing cycle, and your new limits (storage, AI credits, and more) kick in immediately."
                      : "Your switch happens right away. You'll get a credit for the unused time on your current plan, and your new limits apply from today."}{" "}
                    Everything else about your account stays the same.
                  </DialogDescription>
                </DialogHeader>
                <div class="rounded-md border border-border bg-muted/40 px-4 py-3 space-y-1.5 text-sm">
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-muted-foreground">Current plan</span>
                    <span class="font-semibold">
                      {currentPlan().name} · {currentPlan().price}
                    </span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-muted-foreground">New plan</span>
                    <span class="font-semibold">
                      {target.name} · {target.price}
                    </span>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    disabled={loadingTier() !== null}
                    onClick={() => setConfirmTier(null)}
                  >
                    Keep my current plan
                  </Button>
                  <Button
                    disabled={loadingTier() !== null}
                    onClick={() => {
                      void changePlan(tier());
                      setConfirmTier(null);
                    }}
                  >
                    {loadingTier() === tier()
                      ? "Switching…"
                      : `Switch to ${target.name}`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            );
          }}
        </Show>
      </Dialog>

      <Dialog
        open={confirmResume()}
        onOpenChange={(open) => !open && setConfirmResume(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Keep your subscription?</DialogTitle>
            <DialogDescription>
              Your plan won't cancel — you'll stay on {currentPlan().name} and
              keep your current limits after your billing period ends, and
              you'll continue to be billed {currentPlan().price} per month.
            </DialogDescription>
          </DialogHeader>
          <div class="rounded-md border border-border bg-muted/40 px-4 py-3 space-y-1.5 text-sm">
            <div class="flex items-center justify-between gap-3">
              <span class="text-muted-foreground">Plan</span>
              <span class="font-semibold">
                {currentPlan().name} · {currentPlan().price}
              </span>
            </div>
            <div class="flex items-center justify-between gap-3">
              <span class="text-muted-foreground">Scheduled cancel</span>
              <span class="font-semibold">
                {billing().subscriptionCancelAt
                  ? formatDisplayCalendarDate(billing().subscriptionCancelAt!, {
                      style: "short",
                    })
                  : "—"}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={loadingResume()}
              onClick={() => setConfirmResume(false)}
            >
              Let it cancel
            </Button>
            <Button
              disabled={loadingResume()}
              onClick={() => {
                void resumeSubscription();
                setConfirmResume(false);
              }}
            >
              {loadingResume() ? "Keeping…" : "Keep my subscription"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -- Helpers -------------------------------------------------------------------

function InfoRow(props: {
  label: string;
  value: string | null | undefined;
}): JSX.Element {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value || "N/A"}</h6>
    </div>
  );
}
